import React, { useEffect, useState } from "react";
import { Box } from "@mui/material";
import { useWindowSize } from "@uidotdev/usehooks";
import SinConexionKawaii from "../SinConexionKawaii";
import ImagenNoDisponibleKawaii from "../ImagenNoDisponibleKawaii";

// Template64: Día 4 página 2 - Ginza mapa + Mercado de Tsukiji
// Copia EXACTA de Page20.jsx de guia-tokio - contenido fijo, no editable
const Template64 = ({
	backgroundColor,
	textColor,
	headerBackgroundColor,
	headerTextColor,
	fontFamily,
	page,
	updatePage,
	onMapFullscreenChange,
}) => {
	const size = useWindowSize();
	const isSmallScreen = size.width <= 600;
	const [loaded, setLoaded] = useState(
		typeof navigator !== "undefined" ? navigator.onLine : true,
	);
	const [fullscreen, setFullscreen] = useState(false);
	const [isOnline, setIsOnline] = useState(
		typeof navigator !== "undefined" ? navigator.onLine : true,
	);
	const [mercadoError, setMercadoError] = useState(false);
	const [mercadoStickerError, setMercadoStickerError] = useState(false);
	const [palacioError, setPalacioError] = useState(false);

	useEffect(() => {
		const handleOnline = () => {
			setIsOnline(true);
			setLoaded(true);
		};
		const handleOffline = () => {
			setIsOnline(false);
			setLoaded(true);
		};
		window.addEventListener("online", handleOnline);
		window.addEventListener("offline", handleOffline);
		return () => {
			window.removeEventListener("online", handleOnline);
			window.removeEventListener("offline", handleOffline);
		};
	}, []);

	useEffect(() => {
		const style = document.createElement("style");
		style.innerHTML = `
			@keyframes spin {
				0% { transform: rotate(0deg); }
				100% { transform: rotate(360deg); }
			}
		`;
		document.head.appendChild(style);
		return () => document.head.removeChild(style);
	}, []);

	const h3 = {
		fontFamily: "'Poppins', sans-serif",
		fontSize: 14,
		fontWeight: 700,
		textTransform: "uppercase",
		margin: 0,
	};

	const contentColor = textColor || "#444";

	const p = {
		fontFamily: `${fontFamily || "'Poppins', sans-serif"}`,
		fontSize: 13,
		color: contentColor,
		margin: 0,
	};

	const mapUrl = page.maps?.[0];

	if (fullscreen) {
		return (
			<div
				style={{
					position: "fixed",
					top: 0,
					left: 0,
					right: 0,
					bottom: 0,
					backgroundColor: "#fff",
					zIndex: 9999,
					display: "flex",
					flexDirection: "column",
				}}
			>
				<div
					style={{
						padding: "10px",
						textAlign: "right",
						backgroundColor: "#24221a",
					}}
				>
					<button
						onClick={() => {
							setFullscreen(false);
							onMapFullscreenChange?.(false);
						}}
						style={{
							backgroundColor: "#fff",
							color: "#24221a",
							border: "none",
							borderRadius: "8px",
							padding: "6px 12px",
							fontWeight: 600,
							cursor: "pointer",
							fontFamily: "'Poppins', sans-serif",
						}}
					>
						← Volver a la guía
					</button>
				</div>
				{isOnline && mapUrl ? (
					<iframe
						title="Mapa grande"
						src={mapUrl}
						width="100%"
						height="100%"
						style={{ border: 0, flex: 1 }}
						allowFullScreen
						loading="lazy"
					/>
				) : (
					<div
						style={{
							flex: 1,
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
						}}
					>
						<SinConexionKawaii
							height="100%"
							fontFamily={fontFamily || "'Poppins', sans-serif"}
						/>
					</div>
				)}
			</div>
		);
	}

	// Lógica especial para la guía de Tokio: header #24221a en primera página, #e24217 en las siguientes
	const pageOrder =
		page.order !== undefined
			? page.order
			: page.data?.order !== undefined
				? page.data.order
				: -1;
	const isTokioGuide = headerBackgroundColor === "#24221a";
	const actualHeaderColor =
		isTokioGuide && pageOrder > 0 ? "#e24217" : headerBackgroundColor;

	const headerText = page.texts?.[0]?.content;
	const getImageSrc = (id) => {
		if (page.images && Array.isArray(page.images)) {
			const found = page.images.find((img) => img?.id === id);
			if (found && found.src) return found.src;
		}
		return undefined;
	};
	const getCanvasImageUrl = (id) => {
		if (page.canvasImages && Array.isArray(page.canvasImages)) {
			const found = page.canvasImages.find((img) => img?.id === id);
			if (found && found.imageUrl) return found.imageUrl;
		}
		return undefined;
	};
	const mercadoImage = getImageSrc("mercado");
	const palacioImage = page.canvasImages?.find(
		(img) => img.id === "palacio-imperial",
	)?.imageUrl;
	const mercadoStickerImage = getCanvasImageUrl("mercado-sticker");
	const mercadoTitle = page.texts?.[1]?.content;
	const mercadoText = page.texts?.[2]?.content;
	const comoLlegarText = page.texts?.[3]?.content;
	const stickerSushiShort = page.texts?.[4]?.content;
	const stickerSushiLong = page.texts?.[5]?.content;
	const sitiosDestacadosHtml = page.texts?.[6]?.content;

	return (
		<Box
			sx={{
				display: "flex",
				flexDirection: "column",
				minHeight: "100%",
				height: "auto",
				width: "100%",
				backgroundColor,
				color: contentColor,
				position: "relative",
				fontFamily: `${fontFamily} !important`,
				"& *:not([data-template-header])": {
					fontFamily: `${fontFamily} !important`,
					color: "inherit",
				},
				"& p": {
					marginTop: "0px !important",
					marginBottom: "10px !important",
				},
			}}
		>
			{/* CABECERA */}
			<Box
				data-block-id="header"
				data-template-header
				sx={{
					backgroundColor: actualHeaderColor,
					color: headerTextColor,
					textAlign: "center",
					padding: "15px 0",
					fontFamily: `${fontFamily}`,
					fontSize: "14px",
					fontWeight: 800,
					flexShrink: 0,
					position: "sticky",
					top: 0,
					zIndex: 1053,
				}}
			>
				{headerText}
			</Box>

			{/* MAPA */}
			<Box data-block-id="map" sx={{ marginTop: "-70px", position: "relative" }}>
				{/* LOADING */}
				{!loaded && isOnline && (
					<Box
						sx={{
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							height: "100%",
							position: "absolute",
							top: 0,
							left: 0,
							right: 0,
							bottom: 0,
							zIndex: 1,
						}}
					>
						<Box
							sx={{
								width: 20,
								height: 20,
								border: "4px solid #ccc",
								borderTop: "4px solid #e24217",
								borderRadius: "50%",
								animation: "spin 1s linear infinite",
							}}
						/>
					</Box>
				)}

				{/* BOTÓN VER MAPA GRANDE */}
				{isOnline && (
					<button
						onClick={() => {
							setFullscreen(true);
							onMapFullscreenChange?.(true);
						}}
						style={{
							position: "absolute",
							top: 80,
							right: 10,
							backgroundColor: "#24221a",
							color: "#fff",
							padding: "8px 10px",
							fontSize: "11px",
							borderRadius: "8px",
							textDecoration: "none",
							fontFamily: "'Poppins', sans-serif",
							boxShadow: "1px 1px 2px rgba(0,0,0,0.2)",

							border: "none",
							cursor: "pointer",
						}}
					>
						Ver mapa grande
					</button>
				)}

				{isOnline && mapUrl ? (
					<iframe
						title="Mapa Ginza"
						src={mapUrl}
						width="100%"
						height="500px"
						allowFullScreen
						loading="lazy"
						style={{ border: 0 }}
						onLoad={() => setLoaded(true)}
					/>
				) : (
					<SinConexionKawaii
						height="500px"
						fontFamily={fontFamily || "'Poppins', sans-serif"}
					/>
				)}

				{palacioImage && !palacioError && (
					<img
						src={palacioImage}
						alt=""
						onError={() => setPalacioError(true)}
						style={{
							width: 120,
							position: "absolute",
							bottom: -10,
							right: -18,
						}}
					/>
				)}
				{comoLlegarText && (
					<Box
						sx={{
							position: "absolute",
							bottom: 80,
							right: 10,
							backgroundColor: "#fff8db",
							border: "2px solid #f5c400",
							borderRadius: "16px 16px 0px 16px",
							padding: "10px 12px",
							fontSize: 11,
							fontFamily: `${fontFamily} !important`,
							color: contentColor,
							boxShadow: "2px 2px 4px rgba(0,0,0,0.15)",
							maxWidth: 230,
						}}
					>
						{comoLlegarText}
					</Box>
				)}
			</Box>

			{/* MERCADO DE TSUKIJI */}
			<Box
				data-block-id="text-1"
				sx={{
					padding: "14px 12px",
					display: "flex",
					gap: 2,
					position: "relative",
				}}
			>
				{mercadoImage && !mercadoError ? (
					<img
						src={mercadoImage}
						alt="Mercado"
						onError={() => setMercadoError(true)}
						style={{
							width: "45%",
							borderRadius: 12,
							objectFit: "cover",
							height: 110,
						}}
					/>
				) : (
					<ImagenNoDisponibleKawaii
						width="45%"
						height={110}
						fontFamily={fontFamily || "'Poppins', sans-serif"}
						compact
					/>
				)}
				<Box sx={{ width: "60%" }}>
					<Box component="h3" sx={h3}>
						{mercadoTitle}
					</Box>
					{mercadoText && (
						<Box
							component="div"
							dangerouslySetInnerHTML={{ __html: mercadoText }}
							sx={p}
						/>
					)}
				</Box>
				{isSmallScreen && mercadoStickerImage && !mercadoStickerError && (
					<>
						<img
							src={mercadoStickerImage}
							alt=""
							onError={() => setMercadoStickerError(true)}
							style={{
								width: 100,
								position: "absolute",
								top: 70,
								left: -20,
							}}
						/>
						{stickerSushiShort && (
							<Box
								sx={{
									backgroundColor: "#fff8db",
									border: "2px solid #f5c400",
									borderRadius: "16px 16px 16px 0px",
									padding: "10px 12px",
									fontSize: 11,
									fontFamily: `${fontFamily} !important`,
									color: contentColor,
									boxShadow: "2px 2px 4px rgba(0,0,0,0.15)",
									maxWidth: 150,
									position: "absolute",
									top: 50,
									left: 20,
								}}
								component="div"
								dangerouslySetInnerHTML={{ __html: stickerSushiShort }}
							/>
						)}
					</>
				)}
			</Box>

			{/* SITIOS DESTACADOS GINZA */}
			<Box
				data-block-id="text-2"
				sx={{
					padding: "0px 12px",
					display: "flex",
					position: "relative",
				}}
			>
				{!isSmallScreen && mercadoStickerImage && !mercadoStickerError && (
					<>
						<img
							src={mercadoStickerImage}
							alt=""
							onError={() => setMercadoStickerError(true)}
							style={{
								width: 112,
								position: "absolute",
								top: 30,
								right: -32,
							}}
						/>
						{stickerSushiLong && (
							<Box
								sx={{
									backgroundColor: "#fff8db",
									border: "2px solid #f5c400",
									borderRadius: "16px 16px 0px 16px",
									padding: "10px 12px",
									fontSize: 11,
									fontFamily: `${fontFamily} !important`,
									color: contentColor,
									boxShadow: "2px 2px 4px rgba(0,0,0,0.15)",
									maxWidth: 150,
									position: "absolute",
									top: -20,
									right: 0,
								}}
								component="div"
								dangerouslySetInnerHTML={{ __html: stickerSushiLong }}
							/>
						)}
					</>
				)}
				{sitiosDestacadosHtml && (
					<Box
						sx={{
							...p,
							width: "100%",
							"& ul": {
								paddingLeft: "16px",
								marginTop: "8px",
								marginBottom: "16px",
							},
							"& li": { marginBottom: "6px" },
						}}
						component="div"
						dangerouslySetInnerHTML={{ __html: sitiosDestacadosHtml }}
					/>
				)}
			</Box>
		</Box>
	);
};

Template64.thumbnail = "/images/template-1.png";
export default Template64;
